import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ApplicationState } from 'store/rootReducer';
import { shortMonthYear } from 'utils/dateUtil';
import acApi from 'axios/ac/acApi';
import { customRangeToApi } from 'views/Admin/_shared/dateApi';
import { PeriodSelection } from 'views/Admin/UsageReport/types';
import {
  CalculatorSettings,
  HitRateStatus,
  RoiFilters,
  RoiInsightsData,
  RoiSummaryRequest,
  RoiSummaryResponse,
} from '../types';

interface UseRoiInsightsResult {
  data: RoiInsightsData | null;
  isLoading: boolean;
  error: Error | null;
}

const PRESET_DAYS: Record<string, number> = {
  LAST_MONTH: 30,
  LAST_QUARTER: 90,
  LAST_6_MONTHS: 180,
  LAST_YEAR: 365,
};

const buildRequest = (filters: RoiFilters): RoiSummaryRequest => {
  const periodPart =
    filters.period.kind === 'preset'
      ? { period: filters.period.value }
      : customRangeToApi(filters.period.from, filters.period.to);
  return {
    ...periodPart,
    workspaceIds: filters.workspaceIds.length > 0 ? filters.workspaceIds : undefined,
  };
};

const inferPeriodDays = (period: PeriodSelection): number => {
  if (period.kind === 'preset') {
    return PRESET_DAYS[period.value] ?? 30;
  }
  const days = (period.to.getTime() - period.from.getTime()) / 86_400_000;
  return Math.max(1, days);
};

const computeStatus = (effectiveHitRate: number): HitRateStatus => {
  if (effectiveHitRate >= 0.7) return 'HEALTHY';
  if (effectiveHitRate >= 0.5) return 'NEEDS_ATTENTION';
  return 'AT_RISK';
};

const formatIdleSinceLabel = (lastUsedIso: string | null): string =>
  lastUsedIso ? `Since ${shortMonthYear(lastUsedIso)} · 0 hits` : 'Never used · 0 hits';

const responseToData = (
  response: RoiSummaryResponse,
  settings: CalculatorSettings,
  workspaceNamesById: Map<number, string>,
  filters: RoiFilters,
): RoiInsightsData => {
  const effectiveHitRate = response.hitRate;
  const hitsServedByVs = Math.round(response.totalRequests * effectiveHitRate);
  const estimatedCostAvoided = Math.round(hitsServedByVs * settings.costPerCall * 100) / 100;
  const periodDays = inferPeriodDays(filters.period);
  const projectedAnnualSavings = Math.round(estimatedCostAvoided * (365 / periodDays));

  return {
    summary: {
      totalRequests: response.totalRequests,
      hitsServedByVs,
      estimatedCostAvoided,
      projectedAnnualSavings,
      effectiveHitRate,
      hitRateStatus: computeStatus(effectiveHitRate),
      activeServiceCount: response.activeServiceCount,
      totalServiceCount: response.totalServiceCount,
    },
    topByVolume: response.topServicesByVolume.map(s => ({
      serviceName: s.serviceName,
      total: s.total,
      hits: 0,
      misses: 0,
      hitRate: 0,
    })),
    topByHitRate: response.topServicesByHitRate.map(s => ({
      serviceName: s.serviceName,
      hits: s.hits,
      total: s.total,
      misses: s.total - s.hits,
      hitRate: s.hitRate,
    })),
    hitMissBreakdown: response.hitsMissesBreakdown.map(s => ({
      serviceName: s.serviceName,
      hits: s.hits,
      misses: s.misses,
      total: s.hits + s.misses,
      hitRate: s.hits + s.misses > 0 ? s.hits / (s.hits + s.misses) : 0,
    })),
    workspaceRequests: response.requestsByWorkspace.map(w => ({
      workspaceName: workspaceNamesById.get(w.workspaceId) ?? '—',
      totalRequests: w.total,
    })),
    locationUtilisation: response.locationUtilisation.map(l => ({
      locationName: l.locationName,
      totalRequests: l.total,
    })),
    idleCandidates: response.idleServices.map(s => ({
      serviceName: s.serviceName,
      idleSinceLabel: formatIdleSinceLabel(s.lastUsed),
      idleSinceIso: s.lastUsed,
    })),
    dataAsOfIso: new Date().toISOString(),
  };
};

export const useRoiInsights = (filters: RoiFilters, settings: CalculatorSettings): UseRoiInsightsResult => {
  const workspaces = useSelector(
    (state: ApplicationState) => state.blazeUserConfig.blazeUserConfig?.user?.workspaces ?? [],
  );
  const workspaceNamesById = useMemo(() => new Map(workspaces.map(w => [w.id, w.name] as const)), [workspaces]);

  const [rawResponse, setRawResponse] = useState<RoiSummaryResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    acApi.adminReporting
      .getRoiSummary(buildRequest(filters))
      .then(response => {
        if (cancelled) return;
        setRawResponse(response.data.result as RoiSummaryResponse);
        setIsLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error(String(err)));
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [filters]);

  const data = useMemo(
    () => (rawResponse ? responseToData(rawResponse, settings, workspaceNamesById, filters) : null),
    [rawResponse, settings, workspaceNamesById, filters],
  );

  return { data, isLoading, error };
};
